import type { ComponentProps } from 'react';
import { useIntl } from 'react-intl';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';
import { useOperationBusyReason } from './useOperationBusyReason';

type OperationToolbarButtonProps = Omit<
    ComponentProps<typeof ToolbarButton>,
    'tooltip'
> & {
    /** The operation's name, shown alone while nothing blocks it. */
    toolName: string;
    /**
     * Why the operation cannot be opened, appended to the name. The busy
     * reason takes its place while the open operation is busy.
     */
    disabledReason?: string;
};

/**
 * A toolbar button that opens an operation. While the open operation is
 * busy it is disabled and its tooltip names the busy reason, whatever
 * `disabled` says, so it cannot replace or close an operation that is
 * previewing or writing.
 */
export default function OperationToolbarButton({
    toolName,
    disabledReason,
    disabled = false,
    ...buttonProps
}: OperationToolbarButtonProps) {
    const intl = useIntl();
    const busyReason = useOperationBusyReason();
    return (
        <ToolbarButton
            {...buttonProps}
            tooltip={buildToolTooltip(
                intl,
                toolName,
                busyReason ?? disabledReason
            )}
            disabled={disabled || busyReason !== undefined}
        />
    );
}
